import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { ChevronDown, X } from "lucide-react";
import TechnicianHeader from "./TechnicianHeader";
import AppointmentCard from "./AppointmentCard";
import { computeRouteSchedule } from "./utils/schedule";
import { formatCurrency, formatHours, formatMinutes } from "./utils";

// The time grid: 08:00 → 18:30 in 30-minute rows (the FR grid).
const SLOT_START_MIN = 8 * 60;
const SLOT_END_MIN = 18 * 60 + 30;
const SLOT_STEP_MIN = 30;

const clockToMinutes = (value) => {
  const [h, m] = String(value || "").split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
};

const minutesToLabel = (total) => {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

function RouteColumn({
  route,
  preset = "dense",
  selectedAppointmentId,
  selectedForApplyIds = [],
  executionByKey,
  onSelectAppointment,
  onToggleApplySelection,
  showReminders = false,
  onQuickAdd,
  onMapRoute,
  stopWord = "job",
  managedNote = "",
}) {
  const { isOver, setNodeRef } = useDroppable({
    id: route.id,
    data: { type: "route", routeId: route.id },
  });
  const [actionsOpen, setActionsOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!actionsOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) setActionsOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [actionsOpen]);

  const scheduledAppointments = useMemo(() => {
    if ((route.stops || []).some((appointment) => appointment?.computedStart)) return route.stops || [];
    return computeRouteSchedule(route.stops || []);
  }, [route.stops]);

  const startOf = (appointment) =>
    clockToMinutes(appointment.computedStart) ?? clockToMinutes(appointment.proposedStart);

  // Bucket each stop into its 30-minute slot (clamped into the band) — the column
  // reads as a time grid, empty rows included.
  const slots = useMemo(() => {
    const buckets = new Map();
    for (let t = SLOT_START_MIN; t <= SLOT_END_MIN; t += SLOT_STEP_MIN) buckets.set(t, []);
    scheduledAppointments.forEach((appointment) => {
      const startMin = startOf(appointment) ?? SLOT_START_MIN;
      const clamped = Math.min(Math.max(startMin, SLOT_START_MIN), SLOT_END_MIN);
      const slotKey = SLOT_START_MIN + Math.floor((clamped - SLOT_START_MIN) / SLOT_STEP_MIN) * SLOT_STEP_MIN;
      buckets.get(slotKey).push(appointment);
    });
    return Array.from(buckets.entries()).map(([minutes, appointments]) => ({ minutes, label: minutesToLabel(minutes), appointments }));
  }, [scheduledAppointments]);

  // BLOCKS / PROJECTS presets (landscaping, trades, events): jobs as
  // duration-proportional blocks, empty time compressed to a slim divider.
  const blocks = useMemo(() => {
    const sorted = [...scheduledAppointments].sort((a, b) => (startOf(a) ?? 0) - (startOf(b) ?? 0));
    const out = [];
    let prevEnd = null;
    sorted.forEach((appt) => {
      const start = startOf(appt);
      const dur = Math.max(15, Number(appt.durationMinutes || 60));
      if (prevEnd != null && start != null && start - prevEnd >= 30) out.push({ type: "gap", minutes: start - prevEnd, key: `gap-${prevEnd}-${start}` });
      out.push({ type: "job", appt, dur, key: appt.id });
      if (start != null) prevEnd = start + dur;
    });
    return out;
  }, [scheduledAppointments]);
  const blockHeight = (dur) => Math.round(Math.max(46, Math.min(Number(dur || 60) * 0.7, 220)));
  const dayBand = (startMin, dur) => {
    if (startMin == null) return "Scheduled";
    const end = startMin + Number(dur || 0);
    if (startMin < 12 * 60 && end > 13 * 60) return "All day";
    if (startMin < 12 * 60) return "Morning";
    if (startMin >= 17 * 60) return "Evening";
    return "Afternoon";
  };
  const projectHeight = (dur) => Math.round(Math.max(84, Math.min(Number(dur || 120) * 0.6, 340)));

  const executionForAppointment = (appointment) =>
    executionByKey.get(String(appointment.crmAppointmentId)) ||
    executionByKey.get(String(appointment.externalId)) ||
    executionByKey.get(String(appointment.id)) ||
    null;

  const distanceScore = Number(route?.distanceScore ?? route?.metrics?.distanceScore ?? route?.metrics?.travelMiles ?? 0);
  const routeValue = formatCurrency(route?.metrics?.routeRevenue || 0);

  const card = (appointment, variant) => (
    <AppointmentCard
      key={appointment.id}
      variant={variant}
      appointment={appointment}
      routeId={route.id}
      selected={String(selectedAppointmentId) === String(appointment.id)}
      selectedForApply={selectedForApplyIds.includes(String(appointment.id))}
      execution={executionForAppointment(appointment)}
      onSelect={onSelectAppointment}
      onToggleSelected={onToggleApplySelection}
      showReminders={showReminders}
    />
  );

  const addLabel = `Add ${stopWord}`;
  const actions = [
    onQuickAdd ? { key: "add", label: addLabel, run: () => onQuickAdd(route) } : null,
    onMapRoute ? { key: "map", label: "Map route", run: () => onMapRoute(route) } : null,
    { key: "details", label: "Route details", run: () => setDetailsOpen(true) },
  ].filter(Boolean);

  return (
    <>
      <section
        ref={setNodeRef}
        className={["dispatch-route-card", preset !== "dense" ? "dispatch-route-card--blocks" : "", route.modified ? "is-modified" : "", isOver ? "is-drop-target" : ""].filter(Boolean).join(" ")}
        style={{ "--route-color": route.routeColor || "var(--accent)" }}
      >
        <div ref={menuRef} className="dispatch-route-header-shell">
          <TechnicianHeader route={{ ...route, stopWord }} />
          <button
            type="button"
            className={`dispatch-route-actions-bar ${actionsOpen ? "is-active" : ""}`}
            onClick={() => setActionsOpen((current) => !current)}
            aria-expanded={actionsOpen}
            aria-label={`Route actions for ${route.technicianName || "route"}`}
          >
            Route actions <ChevronDown size={12} />
          </button>
          {actionsOpen ? (
            <div className="dispatch-route-actions-menu" role="menu" aria-label="Route actions">
              {actions.map((a) => (
                <button key={a.key} type="button" role="menuitem" className="dispatch-route-action-item" onClick={() => { setActionsOpen(false); a.run(); }}>
                  {a.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <SortableContext items={scheduledAppointments.map((appointment) => appointment.id)} strategy={verticalListSortingStrategy}>
          {preset === "projects" ? (
            <div className="dispatch-route-projects">
              {blocks.map((block) =>
                block.type === "gap" ? (
                  <div key={block.key} className="dispatch-block-gap"><span>{formatMinutes(block.minutes)} open</span></div>
                ) : (
                  <div key={block.key} className="dispatch-project-block" data-band={dayBand(startOf(block.appt), block.dur)} style={{ minHeight: projectHeight(block.dur) }}>
                    <div className="dispatch-project-band">
                      <span className="dispatch-project-band-tag">{dayBand(startOf(block.appt), block.dur)}</span>
                      <span className="dispatch-project-band-dur">{formatHours(block.dur)}</span>
                    </div>
                    {card(block.appt, "block")}
                  </div>
                )
              )}
            </div>
          ) : preset === "blocks" ? (
            <div className="dispatch-route-blocks">
              {blocks.map((block) =>
                block.type === "gap" ? (
                  <div key={block.key} className="dispatch-block-gap"><span>{formatMinutes(block.minutes)} open</span></div>
                ) : (
                  <div key={block.key} className="dispatch-block" style={{ minHeight: blockHeight(block.dur) }}>{card(block.appt, "block")}</div>
                )
              )}
            </div>
          ) : (
            <div className="dispatch-grid" role="grid" aria-label={`${route.technicianName || "Route"} time grid`}>
              {slots.map((slot) =>
                slot.appointments.length ? (
                  <div key={slot.minutes} className="dispatch-grid-row has-jobs" role="row">
                    {slot.appointments.map((appointment) => card(appointment, "dense"))}
                  </div>
                ) : (
                  <button
                    key={slot.minutes}
                    type="button"
                    className="dispatch-grid-row is-empty"
                    role="row"
                    title={onQuickAdd ? `${addLabel} at ${slot.label}` : slot.label}
                    aria-label={onQuickAdd ? `${addLabel} at ${slot.label}` : `${slot.label}, open`}
                    onClick={onQuickAdd ? () => onQuickAdd(route, slot.label) : undefined}
                  >
                    <span className="dispatch-grid-time">{slot.label}</span>
                  </button>
                )
              )}
            </div>
          )}
        </SortableContext>

        <footer className="dispatch-route-footer">
          <span>Distance score <strong>{Math.round(distanceScore)}</strong></span>
          <span className="dispatch-route-sep">·</span>
          <span>Value <strong className="dispatch-route-footer-value">{routeValue}</strong></span>
        </footer>
      </section>

      {detailsOpen
        ? createPortal(
            <div className="dispatch-modal" role="dialog" aria-modal="true" aria-label="Route details">
              <button type="button" className="dispatch-modal-backdrop" onClick={() => setDetailsOpen(false)} aria-label="Close route details" />
              <div className="dispatch-modal-card dispatch-modal-card--sm">
                <header className="dispatch-modal-head">
                  <div>
                    <div className="dispatch-header-kicker">Route details</div>
                    <h3>{route.technicianName || "Unassigned"}</h3>
                  </div>
                  <button type="button" className="dispatch-modal-close" onClick={() => setDetailsOpen(false)} aria-label="Close route details"><X size={16} /></button>
                </header>
                <div className="dispatch-modal-body">
                  <div className="dispatch-detail-grid dispatch-modal-detail">
                    <div><span>Route ID</span><strong>{route.routeId || "—"}</strong></div>
                    <div><span>Service day</span><strong>{route.serviceDay || "—"}</strong></div>
                    <div><span>Type</span><strong>{route.routeTitle || "Regular Routes"}</strong></div>
                    <div><span>Appointments</span><strong>{route.metrics.totalStops}</strong></div>
                    <div><span>Booked hours</span><strong>{formatHours(route.metrics.scheduledMinutes)}</strong></div>
                    <div><span>Route hours</span><strong>{formatHours(route.metrics.routeMinutes)}</strong></div>
                    <div><span>Drive time</span><strong>{formatMinutes(route.metrics.travelMinutes)}</strong></div>
                    <div><span>Value</span><strong>{routeValue}</strong></div>
                  </div>
                  {managedNote ? <p className="dispatch-modal-note">{managedNote}</p> : null}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

export default React.memo(RouteColumn);
